import { PackageX, Archive } from "lucide-react";
import { Linkify } from "@/components/Linkify";
import { RichText } from "@/components/RichText";
import { formatDate } from "@/lib/data";
import { shortName, dirFor } from "@/lib/i18n";

export const Logo = ({ url }) =>
  url ? (
    <div className="flex items-center justify-between border-b border-slate-200 pb-4">
      <img data-testid="public-company-logo" src={url} alt="Company logo" className="h-10 w-auto max-w-[180px] object-contain" />
    </div>
  ) : null;

export const KeyFacts = ({ items, sfx = "" }) => {
  const rows = items.filter(([, v]) => v && String(v).trim());
  if (!rows.length) return null;
  return (
    <dl className="mt-6 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white" data-testid={`public-key-facts${sfx}`}>
      {rows.map(([label, value, id]) => (
        <div key={id} className="flex items-baseline justify-between gap-4 px-5 py-4">
          <dt className="text-sm text-slate-500">{label}</dt>
          <dd data-testid={`public-${id}${sfx}`} className="text-end font-mono text-[15px] font-medium text-slate-900">
            <bdi dir="ltr">{value}</bdi>
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const Section = ({ title, text, testId, lang }) =>
  text && text.trim() ? (
    <section data-testid={`public-section-${testId}`} className="mt-4 rounded-lg border border-slate-200 bg-white p-5 sm:p-6">
      <h3 className="uc-tag flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#064E3B]">
        <span className="h-1.5 w-1.5 rounded-full bg-[#064E3B]" />
        {title}
      </h3>
      <div className="mt-2 text-[15px] leading-relaxed text-slate-700">
        <RichText text={text} lang={lang} testId={`public-rich-${testId}`} />
      </div>
    </section>
  ) : null;

const LangDivider = ({ name, code }) => (
  <div className="mb-6 mt-14 flex items-center gap-3" data-testid={`public-lang-tag-${code}`}>
    <span className="rounded-full border border-[#064E3B]/20 bg-emerald-50 px-3 py-1 text-xs font-semibold text-[#064E3B]">{name}</span>
    <span className="h-px flex-1 bg-slate-200" />
  </div>
);

export const LanguageBlock = ({ block, product, first }) => {
  const { code, name, headings: h, data } = block;
  const sfx = first ? "" : `-${code}`;
  const Title = first ? "h1" : "h2";
  return (
    <section id={`lang-${code}`} lang={code} dir={dirFor(code)} className="scroll-mt-16" data-testid={`public-lang-block-${code}`}>
      {first ? <div className="mt-7" /> : <LangDivider name={name} code={code} />}
      <header>
        <p data-testid={`public-brand-name${sfx}`} title={h.brandName} className="uc-tag text-xs font-semibold uppercase tracking-[0.18em] text-[#064E3B]">
          <span className="sr-only">{h.brandName}: </span>{data.brandName}
        </p>
        <Title data-testid={`public-product-name${sfx}`} title={h.productName}
          className={`font-heading mt-2 font-bold leading-tight tracking-tight text-slate-900 ${first ? "text-3xl sm:text-4xl" : "text-2xl sm:text-3xl"}`}>
          <span className="sr-only">{h.productName}: </span>{data.productName}
        </Title>
      </header>
      <KeyFacts sfx={sfx} items={[
        [h.gazetteNo, product.gazetteNo, "gazette-no"],
        [h.gazetteDate, formatDate(product.gazetteDate), "gazette-date"],
        [h.serialNo, product.serialNo, "serial-no"],
      ]} />
      <Section title={h.composition} text={data.composition} testId={`composition${sfx}`} lang={code} />
      <Section title={h.specifications} text={data.specifications} testId={`specifications${sfx}`} lang={code} />
      <Section title={h.dosage} text={data.dosage} testId={`dosage${sfx}`} lang={code} />
    </section>
  );
};

export const JumpChips = ({ blocks }) => (
  <nav className="sticky top-0 z-10 -mx-4 mt-5 bg-[#F8FAFC] px-4 py-2 sm:-mx-6 sm:px-6" data-testid="public-lang-chips">
    <div className="flex gap-2 overflow-x-auto">
      {blocks.map((b) => (
        <button key={b.code} type="button" lang={b.code} dir={dirFor(b.code)} data-testid={`public-lang-chip-${b.code}`}
          onClick={() => document.getElementById(`lang-${b.code}`)?.scrollIntoView({ behavior: "smooth" })}
          className="shrink-0 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:border-[#064E3B] hover:text-[#064E3B]">
          {shortName(b.name)}
        </button>
      ))}
    </div>
  </nav>
);

export const NotFound = ({ logoUrl }) => (
  <div className="pub-root min-h-screen bg-[#F8FAFC]">
    <main className="mx-auto max-w-xl px-4 pt-5 sm:px-6">
      <Logo url={logoUrl} />
      <div data-testid="product-not-found" className="pub-fade mt-16 rounded-lg border border-slate-200 bg-white px-6 py-12 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
          <PackageX className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <h1 className="font-heading mt-5 text-2xl font-bold tracking-tight text-slate-900">Product Not Found</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">
          This QR code does not match any registered product. Please check the code or contact the manufacturer.
        </p>
      </div>
    </main>
  </div>
);

export const ContactFooter = ({ text, label }) =>
  text?.trim() ? (
    <footer data-testid="public-company-contact" lang="en" dir="ltr" className="mt-12 rounded-lg bg-[#064E3B] p-6 text-emerald-50">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-200">{label}</p>
      <Linkify text={text} className="mt-3 text-[15px] leading-relaxed" />
    </footer>
  ) : null;

export const Discontinued = ({ product: p, settings, contactLabel }) => (
  <div className="pub-root min-h-screen bg-[#F8FAFC]" data-testid="product-discontinued">
    <main className="pub-fade mx-auto max-w-xl px-4 pb-10 pt-5 sm:px-6">
      <Logo url={settings?.logoUrl} />
      <div className="mt-10 rounded-lg border border-slate-200 bg-white px-6 py-10 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-700">
          <Archive className="h-7 w-7" strokeWidth={1.5} />
        </div>
        <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-[#064E3B]" data-testid="discontinued-brand">{p.brandName}</p>
        <h1 className="font-heading mt-2 text-2xl font-bold tracking-tight text-slate-900" data-testid="discontinued-name">{p.productName}</h1>
        <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-slate-600" data-testid="discontinued-notice">
          This product has been discontinued.
        </p>
      </div>
      <ContactFooter text={p.companyContact} label={contactLabel} />
    </main>
  </div>
);
